import { Component, OnInit, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, of, from, filter } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { AgremoService } from '../../core/api-services/agremo.service';
import { AgremoMapService } from '../../core/data-services/agremo-map.service';
import { FieldService } from '../../core/data-services/field.service';
import { FieldOption } from '../../core/response-models/field-option.model';
import { AgremoAnalysisType } from '../../core/response-models/agremo-analysis-type.model';
import { AgremoMap } from '../../core/data-models/agremo-map.model';
import { CreateAnalysisDto } from '../../core/request-models/create-analysis-dto.model';
import { MapAnalysisProductService } from '../../core/api-services/map-analysis-product.service';
import { MapAnalysisQuote } from '../../core/response-models/map-analysis-quote.model';
import { MapAnalysisCheckoutService } from '../../core/api-services/map-analysis-checkout.service';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

/**
 * Analysis Form Component
 *
 * Multi-step wizard for requesting a new Agremo analysis.
 *
 * Steps:
 * 1. Field selection (dropdown with existing fields)
 * 2. Map selection (dropdown of ready maps for the selected field)
 * 3. Analysis type selection, crop type, seeding density
 * 4. Review
 * 5. Payment — Pay & Run Analysis (paywall lands here per billing-payments
 *    spec § 9.4 + § 14.2; boundary + map work in earlier steps stays free)
 *
 * Route: /analyses/new
 */
@Component({
  selector: 'app-analysis-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, MaxLengthDirective],
  templateUrl: './analysis-form.component.html',
  styleUrls: ['./analysis-form.component.scss']
})
export class AnalysisFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private agremoService = inject(AgremoService);
  private agremoMapService = inject(AgremoMapService);
  private fieldService = inject(FieldService);
  private productService = inject(MapAnalysisProductService);
  private checkoutService = inject(MapAnalysisCheckoutService);
  private destroyRef = inject(DestroyRef);

  // Wizard step tracking
  currentStep = signal<number>(1);
  totalSteps = 5;

  // Per-acre pricing — billing-payments § 9. The Hub returns the live
  // unit_amount from Stripe; we cache the most recent quote here so the
  // template can show "$X.XX per acre" up top and "X acres × $Y.YY = $Z.ZZ"
  // on the payment step. Quote refreshes on every field selection (acreage
  // changes the billed acres / total).
  quote = signal<MapAnalysisQuote | null>(null);
  quoteLoading = signal<boolean>(false);
  quoteError = signal<string | null>(null);
  /**
   * Monotonic counter for in-flight quote requests. Each `refreshQuote()`
   * call increments and remembers its own id; when the promise resolves,
   * we only apply the result if no newer request has been issued in the
   * meantime. Prevents the stale-quote race where the user changes fields
   * faster than the Hub responds.
   */
  private quoteRequestId = 0;

  // Checkout state for Step 5
  isCheckoutInFlight = signal<boolean>(false);
  checkoutStage = signal<'idle' | 'creating-session' | 'redirecting'>('idle');
  checkoutError = signal<string | null>(null);

  // Form groups per step
  fieldForm!: FormGroup;
  mapForm!: FormGroup;
  analysisForm!: FormGroup;

  // Data
  fields = signal<FieldOption[]>([]);
  analysisTypes = signal<AgremoAnalysisType[]>([]);
  maps = signal<AgremoMap[]>([]);
  isLoadingMaps = signal(false);

  // (Removed 2026-05-26: `isSubmitting` + `submitError` signals were orphans
  // after the payAndSubmit refactor — checkout-driven failures are surfaced
  // via `checkoutError` and `isCheckoutInFlight` instead. The old Step 4
  // submit banner gated on `submitError` was unreachable.)

  // Local analysis type descriptions (keyed by name from Agremo API)
  readonly analysisTypeDescriptions: Record<string, string> = {
    'Weed Detection': 'Detects weed pressure across the field using high-resolution imagery. Helps optimize herbicide application by identifying problem areas.',
    'Waterlogging Detection': 'Identifies areas of standing water or saturated soil in the field. Helps assess drainage issues and plan remediation before crop damage worsens.',
    'Stand Count': 'Counts individual plants and estimates emergence rate. Best used early in the growing season (VE-V6) to assess planting success and identify gaps. Requires seeding density for accuracy.',
    'Canopy Cover': 'Measures the percentage of ground covered by crop canopy. Useful for evaluating crop establishment and canopy closure timing.',
    'VRS Stand Count': 'Variable Rate Seeding stand count analysis. Compares actual plant populations against target seeding rates to evaluate VRS prescription performance.',
    'Plant Vigor': 'Assesses overall plant vigor and growth variability across the field. Identifies zones of strong and weak growth for targeted scouting or variable rate applications.',
    'Green Snap Detection': 'Detects green snap (stalk breakage) damage caused by high winds during rapid growth stages. Helps quantify yield loss and support insurance claims.',
    'Lodging Detection': 'Identifies areas where crops have lodged (fallen over) due to wind, rain, or weak stalks. Useful for harvest planning and assessing storm damage.',
  };

  async ngOnInit(): Promise<void> {
    // Initialize forms synchronously BEFORE any await so template bindings
    // ([formGroup]) never reference undefined form groups during rendering.
    this.initForms();

    const configured = await this.agremoService.checkStatus();
    if (configured !== true) {
      this.router.navigate(['/analyses']);
      return;
    }
    this.loadData();
    // Prime the quote with acres=1 so the "per-acre" pricing explainer at
    // the top of the page has a real number even before a field is picked.
    // It gets refreshed with real acreage on field selection.
    this.refreshQuote(1);
  }

  private initForms(): void {
    this.fieldForm = this.fb.group({
      fieldId: ['', Validators.required]
    });

    this.mapForm = this.fb.group({
      mapId: ['', Validators.required]
    });

    this.analysisForm = this.fb.group({
      analysisTypeId: ['', Validators.required],
      cropType: [''],
      cropVariety: [''],
      growingStage: [''],
      seedingDensity: [null]
    });
  }

  private async loadData(): Promise<void> {
    // Set up field selection subscription BEFORE async loads to avoid race conditions
    // where the subscription isn't registered yet when the user selects a field.
    this.fieldForm.get('fieldId')?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap((fieldId: string) => {
        // Reset map selection on every field change
        this.mapForm.patchValue({ mapId: '' });
        this.maps.set([]);

        if (!fieldId) {
          return of(null);
        }

        // Prefill crop type from field
        const field = this.fields().find(f => f.id === fieldId);
        if (field?.cropType) {
          this.analysisForm.patchValue({ cropType: field.cropType });
        }

        // Refresh the per-acre quote with the selected field's actual
        // acreage so the order summary and CTA show the right total.
        // If the field is missing/0 acreage, clear the previous quote
        // and surface an error — otherwise the Pay CTA would stay
        // enabled showing a stale per-acre / total from the prior
        // selection, and the user could check out against the wrong
        // price.
        //
        // ALSO bump quoteRequestId so any in-flight refreshQuote() from
        // the previous valid-acreage selection resolves into a no-op
        // (see refreshQuote: it checks `requestId !== this.quoteRequestId`
        // before applying its result). Without the bump, the in-flight
        // request could land after this clear and repopulate `quote()`
        // with the OLD field's pricing — re-enabling isQuoteReady against
        // a field with no acreage on file.
        if (field?.acreage && field.acreage > 0) {
          this.refreshQuote(field.acreage);
        } else {
          ++this.quoteRequestId;
          this.quote.set(null);
          this.quoteLoading.set(false);
          this.quoteError.set(
            field
              ? 'This field has no acreage on file — set the acreage before requesting an analysis.'
              : 'Please select a field with a valid acreage.',
          );
        }

        // Load ready maps — switchMap cancels previous in-flight request
        this.isLoadingMaps.set(true);
        return from(this.agremoMapService.getReadyMaps(fieldId));
      })
    ).subscribe({
      next: (readyMaps) => {
        if (readyMaps) {
          this.maps.set(readyMaps);
        }
        this.isLoadingMaps.set(false);
      },
      error: () => {
        this.isLoadingMaps.set(false);
      }
    });

    // Load field options for dropdown (includes hasBoundary for boundary validation)
    await this.fieldService.refreshFieldOptions();
    this.fields.set(this.fieldService.fieldOptions());

    // Load analysis types
    await this.agremoService.loadAnalysisTypes();
    this.analysisTypes.set(this.agremoService.analysisTypes());
  }

  // ============================================
  // BOUNDARY CHECK
  // ============================================

  get selectedFieldHasBoundary(): boolean {
    const field = this.selectedField;
    if (!field) return false;
    return field.hasBoundary;
  }

  // ============================================
  // STEP NAVIGATION
  // ============================================

  get canGoNext(): boolean {
    switch (this.currentStep()) {
      case 1: return this.fieldForm.valid && this.selectedFieldHasBoundary;
      case 2: return this.mapForm.valid;
      case 3: return this.analysisForm.valid;
      case 4: return true; // Review step — advance into Payment
      case 5: return true; // Payment step terminates via payAndSubmit(), not Next
      default: return false;
    }
  }

  nextStep(): void {
    if (this.canGoNext && this.currentStep() < this.totalSteps) {
      this.currentStep.update(step => step + 1);
    }
  }

  previousStep(): void {
    if (this.currentStep() > 1) {
      this.currentStep.update(step => step - 1);
    }
  }

  goToStep(step: number): void {
    if (step <= this.currentStep() && step >= 1) {
      this.currentStep.set(step);
    }
  }

  isStepComplete(step: number): boolean {
    switch (step) {
      case 1: return this.fieldForm.valid && this.selectedFieldHasBoundary;
      case 2: return this.mapForm.valid;
      case 3: return this.analysisForm.valid;
      case 4: return this.currentStep() > 4;
      default: return false;
    }
  }

  isStepActive(step: number): boolean {
    return this.currentStep() === step;
  }

  // ============================================
  // ANALYSIS TYPE HELPERS (Step 3)
  // ============================================

  get selectedAnalysisType(): AgremoAnalysisType | undefined {
    const id = this.analysisForm.get('analysisTypeId')?.value;
    // Use == for comparison because <select> returns string but API returns number
    return this.analysisTypes().find(t => t.id == id);
  }

  get requiresSeedingDensity(): boolean {
    return this.selectedAnalysisType?.requiresSeedingDensity ?? false;
  }

  get selectedAnalysisTypeDescription(): string | null {
    const name = this.selectedAnalysisType?.name;
    if (!name) return null;
    return this.analysisTypeDescriptions[name] || this.selectedAnalysisType?.description || null;
  }

  onAnalysisTypeChange(): void {
    if (this.requiresSeedingDensity) {
      this.analysisForm.get('seedingDensity')?.setValidators([Validators.required, Validators.min(1)]);
    } else {
      this.analysisForm.get('seedingDensity')?.clearValidators();
      this.analysisForm.get('seedingDensity')?.setValue(null);
    }
    this.analysisForm.get('seedingDensity')?.updateValueAndValidity();
  }

  // ============================================
  // REVIEW HELPERS (Step 4)
  // ============================================

  get selectedField(): FieldOption | undefined {
    const fieldId = this.fieldForm.get('fieldId')?.value;
    return this.fields().find(f => f.id === fieldId);
  }

  get selectedMap(): AgremoMap | undefined {
    const mapId = this.mapForm.get('mapId')?.value;
    return this.maps().find(m => m.id === mapId);
  }

  get reviewSummary(): {
    fieldName: string;
    mapName: string;
    mapDate: string;
    cameraType: string;
    analysisType: string;
    cropType: string;
    cropVariety: string;
    growingStage: string;
    seedingDensity: number | null;
  } {
    return {
      fieldName: this.selectedField?.name || 'Unknown',
      mapName: this.selectedMap?.mapName || 'Unknown',
      mapDate: this.selectedMap?.mapDate || '',
      cameraType: this.selectedMap?.cameraType || 'RGB',
      analysisType: this.selectedAnalysisType?.name || 'Unknown',
      cropType: this.analysisForm.get('cropType')?.value || '',
      cropVariety: this.analysisForm.get('cropVariety')?.value || '',
      growingStage: this.analysisForm.get('growingStage')?.value || '',
      seedingDensity: this.analysisForm.get('seedingDensity')?.value
    };
  }

  // ============================================
  // PAYMENT + SUBMIT (Step 5)
  // ============================================

  /**
   * Pay & Run Analysis — the paywall CTA.
   *
   * Flow (real Hub + Stripe — billing-payments § 9):
   *   1. Mint a fresh Stripe Checkout Session via the Hub with the selected
   *      field's acres + fieldId. Server-side, the Hub computes
   *      quantity = max(1, ceil(acres)) — locking the dollar amount at
   *      session creation against the per-acre Stripe Price.
   *   2. Stash the in-progress analysis form so the return route can finish
   *      creating the analysis after Stripe redirects back.
   *   3. window.location.assign(session.url) — hand off to Stripe.
   *   4. After payment, Stripe redirects to STRIPE_RETURN_BASE_URL_WEB +
   *      /map-analysis/purchase/return?session_id=... where the return-route
   *      component restores the form payload, calls agremoService.createAnalysis,
   *      and routes to the analysis detail page.
   */
  async payAndSubmit(): Promise<void> {
    if (this.isCheckoutInFlight()) return;

    const field = this.selectedField;
    if (!field?.id || !field.acreage || field.acreage <= 0) {
      this.checkoutError.set('Please select a field with a valid acreage before paying.');
      return;
    }

    // Pre-flight guard against the post-payment failure mode: if the
    // map-form or analysis-form is invalid (or the two key ids are blank),
    // the return route would mint a Stripe charge and then fail to create
    // the downstream analysis — leaving the customer with a paid purchase
    // and no analysis. The stepper *should* prevent reaching Step 5 with
    // either form invalid, but a savvy user (or a future refactor that
    // loosens the stepper guards) could still get here. Defending in depth
    // at the CTA is cheap insurance against an unrefundable mismatch.
    const mapId = this.mapForm.get('mapId')?.value;
    const analysisTypeId = this.analysisForm.get('analysisTypeId')?.value;
    if (
      !this.mapForm.valid ||
      !this.analysisForm.valid ||
      typeof mapId !== 'string' ||
      mapId.length === 0 ||
      typeof analysisTypeId !== 'string' ||
      analysisTypeId.length === 0
    ) {
      this.checkoutError.set(
        'Please complete the map and analysis details before paying.',
      );
      return;
    }

    this.isCheckoutInFlight.set(true);
    this.checkoutError.set(null);

    try {
      // Step 1: Stash the analysis payload so the return route can finalize
      // after Stripe redirects back. SessionStorage is fine here — the
      // round-trip happens in the same browser tab. mapId + analysisTypeId
      // are guaranteed non-empty strings here thanks to the guard above.
      const dto: CreateAnalysisDto = {
        mapId,
        analysisTypeId,
        cropType: this.analysisForm.get('cropType')?.value || undefined,
        cropVariety: this.analysisForm.get('cropVariety')?.value || undefined,
        growingStage: this.analysisForm.get('growingStage')?.value || undefined,
        seedingDensity: this.analysisForm.get('seedingDensity')?.value || undefined,
      };
      sessionStorage.setItem('pendingMapAnalysisDto', JSON.stringify(dto));

      // Step 2: Mint a fresh checkout session (every CTA tap, no reuse)
      this.checkoutStage.set('creating-session');
      const session = await this.checkoutService.createCheckoutSession({
        acres: field.acreage,
        fieldId: field.id,
      });

      // Step 3: Hand off to Stripe.
      this.checkoutStage.set('redirecting');
      this.checkoutService.redirectToCheckout(session);
      // Execution stops here as the browser navigates away.
    } catch (error: any) {
      console.error('Pay & submit error:', error);
      this.checkoutError.set(error.message || 'An error occurred during checkout. Please try again.');
      this.isCheckoutInFlight.set(false);
      this.checkoutStage.set('idle');
      sessionStorage.removeItem('pendingMapAnalysisDto');
    }
  }

  /**
   * Refresh the per-acre quote from the Hub for the given acreage. Silent
   * on failure — the order-summary panel handles the missing-quote state.
   *
   * Concurrency-safe: only the most-recent request's result is applied.
   * Older in-flight requests resolve into no-ops if a newer one was
   * issued while they were pending.
   */
  private async refreshQuote(acres: number): Promise<void> {
    const requestId = ++this.quoteRequestId;
    this.quoteLoading.set(true);
    this.quoteError.set(null);
    try {
      const q = await this.productService.getQuote(acres);
      if (requestId !== this.quoteRequestId) return; // a newer request superseded us
      this.quote.set(q);
    } catch (e: any) {
      if (requestId !== this.quoteRequestId) return;
      this.quoteError.set(e?.message || 'Could not load pricing');
    } finally {
      if (requestId === this.quoteRequestId) {
        this.quoteLoading.set(false);
      }
    }
  }

  // Display helpers — keep the template thin.
  get perAcreLabel(): string {
    const q = this.quote();
    if (!q) return '—';
    return this.productService.formatCents(q.unitPriceCents, q.currency) + ' / acre';
  }

  get totalLabel(): string {
    const q = this.quote();
    if (!q) return '—';
    return this.productService.formatCents(q.totalCents, q.currency);
  }

  /**
   * Whether a price quote has loaded AND is current. Used to gate the
   * Pay CTA so:
   *   1. The label never renders "Pay — & Run Analysis" (when quote is
   *      null on initial paint).
   *   2. The CTA stays disabled while a refresh is in flight, so the
   *      user can't click Pay against a stale total that's about to be
   *      replaced by a fresh quote tied to changed inputs (acres edit,
   *      field reselect, etc.).
   *   3. The CTA stays disabled when the latest refresh errored — the
   *      stale `quote()` value is still non-null but no longer reflects
   *      the current selection, so paying against it would mint a Stripe
   *      Checkout session at the wrong price.
   *
   * Three-condition check: quote present + not loading + no error.
   */
  get isQuoteReady(): boolean {
    return this.quote() != null && !this.quoteLoading() && this.quoteError() == null;
  }

  get billedAcresLabel(): string {
    const q = this.quote();
    if (!q) return '—';
    return `${q.billedAcres} acre${q.billedAcres === 1 ? '' : 's'}`;
  }

  goBack(): void {
    this.router.navigate(['/analyses']);
  }
}
